/**
 * Fuzzy enum matching for write-ops field mapping.
 *
 * Used when the user says "priority high" or "status open" and we need
 * to map that natural-language fragment to a PISA enum value.
 *
 * Design decisions (locked):
 * - Levenshtein distance with cutoff max(2, floor(len/3))
 * - Skip fuzzy matching for inputs shorter than 3 characters (too noisy)
 * - Reject candidates whose length differs from the query by more than 1: a real
 *   typo preserves length (transposition "hihg"→"high", one substitution), while a
 *   length jump of 2+ is a different word sharing a substring ("active"→"inactive"
 *   adds the morpheme "in"). Both score distance 2, but only the latter flips
 *   meaning. Since a no-match now falls through to a selection card, a false
 *   positive (silent wrong write) is far costlier than a false negative (show the
 *   card) — so we bias against morpheme collisions. (Same "a wrong guess is worse
 *   than none" principle as the CSV column matcher.)
 * - Tie → null (ambiguous — caller should ask the user to clarify)
 */

import { levenshtein } from './string-distance'

export interface EnumMatchResult {
  value: string
  distance: number
}

/**
 * Find the best matching enum value for a user-supplied query string.
 *
 * @param query      - The raw user input (e.g. "high", "open", "in progress")
 * @param enumValues - The PISA enum value strings to match against
 * @returns The best-matching enum value string, or null if:
 *   - query is shorter than 3 characters (too ambiguous)
 *   - no match is within the cutoff distance
 *   - there is a tie between two or more values at the same distance
 */
export function findBestEnumMatch(
  query: string,
  enumValues: string[],
): string | null {
  if (enumValues.length === 0) return null

  const normalised = query.toLowerCase().trim()

  // Too short — too noisy for fuzzy matching
  if (normalised.length < 3) return null

  // Exact match takes priority (case-insensitive)
  const exact = enumValues.find((v) => v.toLowerCase() === normalised)
  if (exact !== undefined) return exact

  const cutoff = Math.max(2, Math.floor(normalised.length / 3))

  const candidates: EnumMatchResult[] = enumValues
    .map((v) => ({
      value: v,
      distance: levenshtein(normalised, v.toLowerCase()),
    }))
    .filter(
      (c) =>
        c.distance <= cutoff &&
        Math.abs(c.value.length - normalised.length) <= 1,
    )
    .sort((a, b) => a.distance - b.distance)

  if (candidates.length === 0) return null

  const best = candidates[0]!
  // Tie: two or more candidates share the minimum distance → ambiguous
  const tied =
    candidates.length > 1 && candidates[1]!.distance === best.distance
  if (tied) return null

  return best.value
}
